<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import ContactListHeaderWrapper from 'dashboard/components-next/Contacts/ContactsHeader/ContactListHeaderWrapper.vue';
import ContactsActiveFiltersPreview from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactsActiveFiltersPreview.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import ContactsLoadMore from 'dashboard/components-next/Contacts/ContactsLoadMore.vue';

const props = defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  showPaginationFooter: { type: Boolean, default: true },
  currentPage: { type: Number, default: 1 },
  totalItems: { type: Number, default: 100 },
  itemsPerPage: { type: Number, default: 15 },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
  activeSegment: { type: Object, default: null },
  segmentsId: { type: [String, Number], default: 0 },
  hasAppliedFilters: { type: Boolean, default: false },
  isFetchingList: { type: Boolean, default: false },
  useInfiniteScroll: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  isLoadingMore: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:currentPage',
  'update:sort',
  'search',
  'applyFilter',
  'clearFilters',
  'loadMore',
]);

const route = useRoute();

const contactListHeaderWrapper = ref(null);

const isNotSegmentView = computed(() => {
  return route.name !== 'contacts_dashboard_segments_index';
});

const isActiveView = computed(() => {
  return route.name === 'contacts_dashboard_active';
});

const isLabelView = computed(
  () => route.name === 'contacts_dashboard_labels_index'
);

const showActiveFiltersPreview = computed(() => {
  return (
    (props.hasAppliedFilters || !isNotSegmentView.value) &&
    !props.isFetchingList &&
    !isLabelView.value &&
    !isActiveView.value
  );
});

const updateCurrentPage = page => {
  emit('update:currentPage', page);
};

const openFilter = () => {
  contactListHeaderWrapper.value?.onToggleFilters();
};

const showLoadMore = computed(() => {
  return props.useInfiniteScroll && props.hasMore;
});

const showPagination = computed(() => {
  return !props.useInfiniteScroll && props.showPaginationFooter;
});
</script>

<template>
  <section
    class="flex w-full h-full gap-4 overflow-hidden justify-evenly bg-n-background dark:bg-n-black"
  >
    <div class="flex flex-col w-full h-full transition-all duration-300">
      <div class="h-5 bg-n-background dark:bg-n-black shrink-0 w-full" />
      <main class="flex-1 overflow-y-auto px-6 bg-n-surface-1 rounded-t-2xl flex flex-col justify-between">
        <div class="w-full mx-auto max-w-5xl flex-grow py-3">
          <ContactListHeaderWrapper
            ref="contactListHeaderWrapper"
            :show-search="isNotSegmentView && !isActiveView"
            :search-value="searchValue"
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            :header-title="headerTitle"
            :active-segment="activeSegment"
            :segments-id="segmentsId"
            :has-applied-filters="hasAppliedFilters"
            :is-label-view="isLabelView"
            :is-active-view="isActiveView"
            @update:sort="emit('update:sort', $event)"
            @search="emit('search', $event)"
            @apply-filter="emit('applyFilter', $event)"
            @clear-filters="emit('clearFilters')"
            class="!bg-transparent !border-b-0 px-0 pb-2 mb-4 relative z-20"
          />
          <ContactsActiveFiltersPreview
            v-if="showActiveFiltersPreview"
            :active-segment="activeSegment"
            class="mb-1"
            @clear-filters="emit('clearFilters')"
            @open-filter="openFilter"
          />
          <slot name="default" />
          <ContactsLoadMore
            v-if="showLoadMore"
            :is-loading="isLoadingMore"
            @load-more="emit('loadMore')"
          />
        </div>
        <footer v-if="showPagination" class="sticky bottom-0 z-0 pb-4 bg-n-surface-1 w-full max-w-5xl mx-auto">
          <PaginationFooter
            current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
            :current-page="currentPage"
            :total-items="totalItems"
            class="max-w-[67rem]"
            :items-per-page="itemsPerPage"
            @update:current-page="updateCurrentPage"
          />
        </footer>
      </main>
    </div>
  </section>
</template>
